<script setup>
import { computed } from 'vue';
import EventModalDateRange from '@/js/core/components/pages/event-modal/EventModalDateRange.vue';
import {
  eventModalAffiliationItems,
  eventModalCourseSections,
  eventModalOccurrenceGroups,
} from '@/js/core/utils/event-modal-meta';
import { hasZoom, locationWithZoom } from '@/js/core/utils/calendar-events';

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  timezone: {
    type: String,
    default: '',
  },
  clock: {
    type: Number,
    default: 0,
  },
});

const locationText = computed(() => {
  const eventLocations = Array.isArray(props.event.locations) ? props.event.locations : [];
  const baseLocation = props.event.location || eventLocations.join(', ');

  return locationWithZoom(baseLocation, hasZoom(props.event));
});

const hasLocation = computed(() => locationText.value !== '');
const occurrenceGroups = computed(() =>
  eventModalOccurrenceGroups(props.event, {
    timezone: props.timezone,
    clock: props.clock || Date.now(),
  })
);
const affiliationItems = computed(() => eventModalAffiliationItems(props.event));
const audienceLabels = computed(() =>
  Array.isArray(props.event.audience_labels) ? props.event.audience_labels : []
);
const courseSections = computed(() => eventModalCourseSections(props.event));
</script>

<template>
  <div class="event-modal__meta-info-container">
    <ul class="event-modal__meta-info">
      <li v-if="occurrenceGroups.length" class="event-modal__meta-info-row">
        <div
          v-for="group in occurrenceGroups"
          :key="group.key"
          :class="`event-modal__meta-${group.key}`"
        >
          <span class="event-modal__info-row-heading">{{ group.label }}</span>
          <div class="event-modal__info-row-content time-date">
            <ul class="event-modal__occurrence-list">
              <li
                v-for="occurrence in group.items"
                :key="occurrence.key"
                class="event-modal__occurrence"
              >
                <EventModalDateRange :item="occurrence" />
                <span v-if="occurrence.semanticLabel" class="event-modal__is-today">
                  {{ occurrence.semanticLabel }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </li>
      <li v-if="hasLocation" class="event-modal__meta-info-row">
        <span class="event-modal__info-row-heading">Location</span>
        <span class="event-modal__info-row-content">{{ locationText }}</span>
      </li>
      <li v-if="affiliationItems.length" class="event-modal__meta-info-row">
        <span class="event-modal__info-row-heading">Affiliations</span>
        <span class="event-modal__info-row-content">{{ affiliationItems.join(', ') }}</span>
      </li>
      <li v-if="audienceLabels.length" class="event-modal__meta-info-row">
        <span class="event-modal__info-row-heading">Audiences</span>
        <span class="event-modal__info-row-content">{{ audienceLabels.join(', ') }}</span>
      </li>
      <li v-if="courseSections.length" class="event-modal__meta-info-row last-box">
        <template v-for="section in courseSections" :key="section.label">
          <span class="event-modal__info-row-heading">{{ section.label }}</span>
          <span class="event-modal__info-row-content">{{ section.values.join(', ') }}</span>
        </template>
      </li>
    </ul>
  </div>
</template>
